// Pricing data for the /pricing page.
//
// This used to be fetched at runtime from the ai-web-gen backend
// (GET https://web-gen.cero-ai.com/api/public/pricing). That endpoint lived
// inside ai-web-gen's mail module and was removed when the mail feature was
// deleted, so the fetch started failing ("Load failed"). The data is small,
// static, and owned by this site, so it now lives here directly — no network,
// no CORS, no runtime failure mode.

export interface Package {
  id: string;
  number: string;
  name: string;
  desc: string;
  basePrice: number | null;
  popular: boolean;
  features: string[];
}

export interface AboTier {
  id: string;
  eyebrow: string;
  name: string;
  description: string;
  discountPercent: number;
  monthlyPrice: number;
  durationMonths: number;
  features: string[];
}

// The AI layer is sold as two plans (Basic / Pro) with 1, 6 or 12-month terms.
// monthlyPrice is the 12-month-term price, shown as "from". Prices include VAT and
// mirror ai-web-gen/src/shared/billing.ts.
export interface KiPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  popular: boolean;
  features: string[];
}

export interface PricingData {
  packages: Package[];
  aboTiers: AboTier[];
  kiPlans: KiPlan[];
}

export type PricingLang = "de" | "en";

// Prices carry cents now (12,99 / 36,99), so they need locale-aware formatting.
export function formatEuro(value: number, lang: PricingLang): string {
  return new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export const PRICING_DE: PricingData = {
  kiPlans: [
    {
      id: "basic",
      name: "Basic",
      monthlyPrice: 29,
      popular: false,
      features: [
        "KI-Chatbot auf Ihrer Website, trainiert auf Ihre Inhalte",
        "1.000 Antworten/Monat",
        "€49 monatlich kündbar · €39 bei 6 Monaten · €29 bei 12 Monaten",
        "Zusatzpaket mit 1.000 Antworten: €20",
      ],
    },
    {
      id: "pro",
      name: "Pro",
      monthlyPrice: 79,
      popular: true,
      features: [
        "Alles aus Basic, plus:",
        "4.000 Antworten/Monat",
        "€129 monatlich kündbar · €99 bei 6 Monaten · €79 bei 12 Monaten",
        "Alternativ nutzungsbasiert: €0,02 pro Antwort",
      ],
    },
  ],
  aboTiers: [
    {
      id: "maintenance",
      eyebrow: "Technisch + Inhaltlich + SEO",
      name: "Wartungsvertrag",
      description: "Wir wachsen mit Ihnen.",
      discountPercent: 10,
      monthlyPrice: 29,
      durationMonths: 12,
      features: [
        "Sicherheitsupdates",
        "Backups",
        "Uptime-Monitoring",
        "SSL-Verlängerung",
        "Technischer Support bei Problemen",
        "Inhaltliche Pflege",
        "Monatliche SEO-Optimierung",
        "Performance-Reports",
        "Strategische Beratung",
      ],
    },
  ],
  packages: [
    {
      id: "starter",
      number: "01",
      name: "Starter",
      desc: "Professionelle Online-Präsenz für kleine Unternehmen und Selbstständige.",
      basePrice: 149,
      popular: false,
      features: [
        "1–3 individuell gestaltete Seiten",
        "Impressum & Datenschutz (DSGVO)",
        "Responsive Design (Mobil + Desktop)",
        "Kontaktformular",
        "SSL-Zertifikat & Hosting-Setup",
      ],
    },
    {
      id: "medium",
      number: "02",
      name: "Medium",
      desc: "Für wachsende Unternehmen, die mehr als eine Visitenkarte brauchen.",
      basePrice: 599,
      popular: true,
      features: [
        "Alles aus Starter, plus:",
        "3–15 Unterseiten",
        "Blog / News-Bereich",
        "Backend-Login & Content-Management",
        "Analytics-Integration",
        "SEO-Grundoptimierung",
        "Cookie-Banner & DSGVO-Compliance",
      ],
    },
    {
      id: "custom",
      number: "03",
      name: "Custom",
      desc: "Maßgeschneiderte Web-Lösung für komplexe Anforderungen.",
      basePrice: null,
      popular: false,
      features: [
        "Alles aus Medium, plus:",
        "15+ Unterseiten",
        "Individuelle Features & Integrationen",
        "Multi-Language / i18n",
        "API-Anbindungen & Automatisierung",
        "Dedizierter Ansprechpartner",
      ],
    },
  ],
};

export const PRICING_EN: PricingData = {
  kiPlans: [
    {
      id: "basic",
      name: "Basic",
      monthlyPrice: 29,
      popular: false,
      features: [
        "AI chatbot on your website, trained on your content",
        "1,000 answers/month",
        "€49 month to month · €39 on 6 months · €29 on 12 months",
        "Extra pack of 1,000 answers: €20",
      ],
    },
    {
      id: "pro",
      name: "Pro",
      monthlyPrice: 79,
      popular: true,
      features: [
        "Everything in Basic, plus:",
        "4,000 answers/month",
        "€129 month to month · €99 on 6 months · €79 on 12 months",
        "Or pay per use: €0.02 per answer",
      ],
    },
  ],
  aboTiers: [
    {
      id: "maintenance",
      eyebrow: "Technical + content + SEO",
      name: "Maintenance plan",
      description: "We grow with you.",
      discountPercent: 10,
      monthlyPrice: 29,
      durationMonths: 12,
      features: [
        "Security updates",
        "Backups",
        "Uptime monitoring",
        "SSL renewal",
        "Technical support when issues arise",
        "Content updates",
        "Monthly SEO optimisation",
        "Performance reports",
        "Strategic advisory",
      ],
    },
  ],
  packages: [
    {
      id: "starter",
      number: "01",
      name: "Starter",
      desc: "Professional online presence for small businesses and freelancers.",
      basePrice: 149,
      popular: false,
      features: [
        "1 to 3 custom-designed pages",
        "Imprint & privacy policy (GDPR)",
        "Responsive design (mobile + desktop)",
        "Contact form",
        "SSL certificate & hosting setup",
      ],
    },
    {
      id: "medium",
      number: "02",
      name: "Medium",
      desc: "For growing companies that need more than a digital business card.",
      basePrice: 599,
      popular: true,
      features: [
        "Everything in Starter, plus:",
        "3 to 15 subpages",
        "Blog / news section",
        "Backend login & content management",
        "Analytics integration",
        "Basic SEO optimisation",
        "Cookie banner & GDPR compliance",
      ],
    },
    {
      id: "custom",
      number: "03",
      name: "Custom",
      desc: "Tailor-made web solution for complex requirements.",
      basePrice: null,
      popular: false,
      features: [
        "Everything in Medium, plus:",
        "15+ subpages",
        "Custom features & integrations",
        "Multi-language / i18n",
        "API integrations & automation",
        "Dedicated point of contact",
      ],
    },
  ],
};

export function getPricing(lang: PricingLang): PricingData {
  return lang === "en" ? PRICING_EN : PRICING_DE;
}
